<script>
    import modalService from '@/services/core/modal.service';
    import Modal from '@/components/modals/Modal';
    import ConfirmModal from '@/components/modals/ConfirmModal';
    import utilsService from '@/services/core/utils.service';
    import httpService from '@/services/core/http.service';
    import AdminGrid from '@/components/shared/AdminGrid';
    import { DxColumn } from 'devextreme-vue/data-grid';

    const STATUS_LABELS = {
        0: 'In Progress',
        1: 'Completed',
        2: 'Failed',
        3: 'Undone',
        4: 'Queued'
    };

    export default {
        props: {
            modalData: Object
        },
        components: {
            Modal,
            AdminGrid,
            DxColumn
        },
        data() {
            return {
                isLoading: 0,
                rows: [],
                detail: null
            };
        },
        computed: {
            statusLabel() {
                if (!this.detail) return '';
                return STATUS_LABELS[this.detail.status] || `Unknown (${this.detail.status})`;
            },
            canUndo() {
                if (!this.detail) return false;
                return this.detail.status === 1 || this.detail.status === 2 || this.detail.status === 4;
            }
        },
        methods: {
            close() {
                modalService.closeModal();
            },
            statusText(status) {
                return STATUS_LABELS[status] || `Unknown (${status})`;
            },
            canUndoRow(row) {
                return row && (row.status === 1 || row.status === 2 || row.status === 4);
            },
            load() {
                this.isLoading++;
                const companyId = this.modalData?.companyId;
                const qs = companyId ? `?companyId=${companyId}&take=100` : '?take=100';

                httpService
                    .get(`facility-clones${qs}`)
                    .then(data => {
                        this.rows = (data || []).map(row => ({
                            ...row,
                            statusLabel: this.statusText(row.status),
                            measurementsLabel: row.includeMeasurements ? 'Yes' : 'No'
                        }));
                    })
                    .finally(() => this.isLoading--);
            },
            openDetail(row) {
                if (!row?.id) return;

                this.isLoading++;
                httpService
                    .get(`facility-clones/${row.id}`)
                    .then(data => {
                        this.detail = data;
                    })
                    .finally(() => this.isLoading--);
            },
            undoFromRow(row) {
                if (!row?.id) return;

                modalService.show(ConfirmModal, {
                    title: 'Undo Facility Clone',
                    msg: `Permanently remove everything created by clone operation <strong>#${row.id}</strong>? This cannot be undone.`,
                    btnTextOk: 'Undo Clone',
                    onSuccess: () => this.runUndo(row.id)
                });
            },
            undo() {
                if (!this.detail) return;

                modalService.show(ConfirmModal, {
                    title: 'Undo Facility Clone',
                    msg: `Permanently remove everything created by clone operation <strong>#${this.detail.id}</strong>? This cannot be undone.`,
                    btnTextOk: 'Undo Clone',
                    onSuccess: () => this.runUndo(this.detail.id)
                });
            },
            runUndo(cloneOperationId) {
                this.isLoading++;
                httpService
                    .post(`facility-clones/${cloneOperationId}/undo`, true)
                    .then(() => {
                        utilsService.showToast('Facility clone undone');
                        return httpService.get(`facility-clones/${cloneOperationId}`);
                    })
                    .then(data => {
                        this.detail = data;
                        return this.load();
                    })
                    .finally(() => this.isLoading--);
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<template>
    <Modal sizeClass="modal-xl">
        <div class="modal-content">
            <div class="modal-header">
                <h3 class="modal-title">Clone History</h3>
            </div>
            <div class="modal-body">
                <p class="text-muted small mb-3">
                    Recent facility clones across companies. Open a row for details; undo is available when status is
                    Completed, Failed, or Queued (cancel before the worker starts).
                </p>

                <AdminGrid
                    :data-source="rows"
                    :is-loading="isLoading > 0"
                    :show-export="false"
                    :show-toolbar="true"
                    :remote-operations="false"
                    storage-key="facility-clone-history"
                >
                    <DxColumn data-field="id" caption="Op #" :width="80" sort-order="desc" />
                    <DxColumn data-field="statusLabel" caption="Status" :width="110" />
                    <DxColumn data-field="sourceCompanyId" caption="Source Co" :width="100" />
                    <DxColumn data-field="sourceBatteryId" caption="Source Battery" :width="120" />
                    <DxColumn data-field="targetCompanyId" caption="Dest Co" :width="100" />
                    <DxColumn data-field="targetBatteryId" caption="Dest Battery" :width="110" />
                    <DxColumn data-field="measurementsLabel" caption="Measurements" :width="120" />
                    <DxColumn data-field="createdOn" caption="Created" data-type="datetime" format="MM/dd/yyyy hh:mm a" />
                    <DxColumn data-field="completedDate" caption="Completed" data-type="datetime" format="MM/dd/yyyy hh:mm a" />
                    <DxColumn data-field="undoneDate" caption="Undone" data-type="datetime" format="MM/dd/yyyy hh:mm a" />

                    <template #actions="{ item }">
                        <button
                            type="button"
                            class="btn btn-link py-0 lh-1"
                            title="Details"
                            @click="openDetail(item.data)"
                        >
                            <i class="bi bi-eye"></i>
                        </button>
                        <button
                            v-if="canUndoRow(item.data)"
                            type="button"
                            class="btn btn-link py-0 lh-1 text-danger"
                            title="Undo clone"
                            :disabled="isLoading > 0"
                            @click="undoFromRow(item.data)"
                        >
                            <i class="bi bi-arrow-counterclockwise"></i>
                        </button>
                    </template>
                </AdminGrid>

                <div v-if="detail" class="border rounded p-3 bg-light mt-3">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <strong>Operation #{{ detail.id }}</strong>
                            <span class="badge bg-secondary ms-2">{{ statusLabel }}</span>
                        </div>
                        <button
                            v-if="canUndo"
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                            :disabled="isLoading > 0"
                            @click="undo"
                        >
                            Undo clone
                        </button>
                    </div>
                    <div class="small">
                        <div>Source company / battery: {{ detail.sourceCompanyId }} / {{ detail.sourceBatteryId }}</div>
                        <div>
                            Target company / battery: {{ detail.targetCompanyId }} /
                            {{ detail.targetBatteryId || '—' }}
                        </div>
                        <div>Include measurements: {{ detail.includeMeasurements ? 'Yes' : 'No' }}</div>
                        <div>Created: {{ detail.createdOn }}</div>
                        <div v-if="detail.completedDate">Completed: {{ detail.completedDate }}</div>
                        <div v-if="detail.undoneDate">Undone: {{ detail.undoneDate }}</div>
                        <div>Mapped entities: {{ (detail.entityMap || []).length }}</div>
                    </div>
                </div>
            </div>
            <div class="modal-footer d-flex justify-content-between">
                <button type="button" class="btn" @click="close">Close</button>
                <button type="button" class="btn btn-outline-secondary" :disabled="isLoading > 0" @click="load">
                    Refresh
                </button>
            </div>
        </div>
    </Modal>
</template>
